import { Injectable } from '@angular/core';
import { ApiService } from '@core/api.service';
import { AuthService } from '@features/auth/auth.service';
import { DefaultTaskItemModel } from '@features/settings/projects/projects.models';
import { CrudService } from 'collection';
import { Collection, DeleteEntityModel, GetEntityModel, Item, PostEntityModel, UpdateEntityModel } from 'collection';
import { CollectionModel, ItemModel } from 'collection';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  isWorker: boolean;
  isOpenSidebar: boolean = true;
  projectItem: ItemModel;
  subProjectItem: ItemModel;

  projectDeviationItem: ItemModel;

  downloadDocument: GetEntityModel;

  downloadDocumentsArchive: PostEntityModel;

  taskBatchDelete: DeleteEntityModel;
  tasksCollection: CollectionModel;
  deviationsCollection: CollectionModel;
  documentsCollection: CollectionModel;
  deviationBatchDelete: DeleteEntityModel;
  documentsBatchDelete: DeleteEntityModel;
  assignUserCollection: CollectionModel;
  checkExistsNames: PostEntityModel;

  isDeviationBelongsToChecklist: GetEntityModel = this._crud.createGetEntity({ name: 'deviations/:id/belongs-to-checklist' });

  private _isOpenSidebar: BehaviorSubject<boolean> = new BehaviorSubject(this.isOpenSidebar);

  get $isOpenSidebar(): Observable<any> {
    return this._isOpenSidebar.asObservable();
  }

  private _selectedMenuCollapse$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  selectedMenuCollapse$ = this._selectedMenuCollapse$.asObservable();

  toggleSidebar(): boolean {
    this.isOpenSidebar = !this.isOpenSidebar;
    this._isOpenSidebar.next(this.isOpenSidebar);
    return this.isOpenSidebar;
  }

  closeSidebar(): void {
    this.isOpenSidebar = false;
    this._isOpenSidebar.next(this.isOpenSidebar);
  }

  openSidebar(): void {
    this.isOpenSidebar = true;
    this._isOpenSidebar.next(this.isOpenSidebar);
  }

  setActiveMenu(menuKey) {
    this._selectedMenuCollapse$.next(menuKey);
  }

  clearActiveMenu() {
    this._selectedMenuCollapse$.next(null);
  }

  constructor(
    private _crud: CrudService,
    private _api: ApiService,
    private _auth: AuthService
  ) {
    this.isWorker = this._auth.isWorker();

    this.downloadDocument = this._crud.createGetEntity({ name: 'project-files/download-object' });
    this.downloadDocumentsArchive = this._crud.createPostEntity({ name: 'project-files/download-archive' });
    this.checkExistsNames = this._crud.createPostEntity({ name: 'project-files/check-names' });

    this.projectItem = new Item({
      api: { path: 'project' },
      params: {
        expand: 'client,user,total_time_minutes,subprojects.client,subprojects.display_status,project_location,estimate'
      }
    });

    this.subProjectItem = new Item({
      api: { path: 'project' },
      params: {
        expand: 'subprojects.display_status'
      }
    });

    this.projectDeviationItem = new Item({
      api: { path: 'deviations' },
      params: {
        expand: 'project,client,tags,user,assigned_user,type,incident,images_count'
      }
    });

    this.taskBatchDelete = this._crud.createDeleteManyEntity({ name: 'task/batch-delete' });
    this.tasksCollection = new Collection({
      api: { path: 'task' },
      params: {
        type: 'project'
      }
    });

    this.tasksCollection.mapItems = (item) => new DefaultTaskItemModel(item);
    this.tasksCollection.hardReloadAfter.creating = false;

    this.deviationsCollection = new Collection({
      api: { path: 'deviations' },
      params: {
        expand: 'tags,user,images_count,assigned_user,type,incident'
      }
    });

    this.documentsCollection = new Collection({
      api: { path: 'project-files' },
      params: {
        expand: 'user,user_name'
      }
    });

    this.deviationBatchDelete = this._crud.createDeleteManyEntity({ name: 'deviations/batch-delete' });
    this.documentsBatchDelete = this._crud.createDeleteManyEntity({ name: 'project-files/batch-delete' });

    const assignUserAPI = this._crud.createEntity({ name: 'project/:project_id/assign-user' });

    const defaultParams = {
      expand: 'full_name',
      role: 'worker,manager'
    };

    if (!this.isWorker) {
      defaultParams['role'] = 'worker,manager,admin';
      defaultParams['assign_all'] = true;
    }

    this.assignUserCollection = new Collection({
      api: assignUserAPI,
      params: defaultParams
    });
  }
}
